import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

export default function MyMemberships() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const member = JSON.parse(localStorage.getItem("member") || "null");

  useEffect(() => {
    const load = async () => {
      try {
        const memberId = member?.id || member?.member_id;

        if (!memberId) {
          setError("Member ID not found in localStorage");
          return;
        }

        const result = await api.post("getMyMembershipApplications.php", {
          member_id: memberId,
        });

        if (result?.success) {
          setItems(result.applications || []);
        } else {
          setError(result?.message || "Failed to fetch memberships");
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const handleEdit = (app) => {
    navigate("/membershipform", {
      state: {
        editMode: true,
        application: app.id ? app : null,
      },
    });
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this application?"
    );

    if (!confirmDelete) return;

    try {
      const result = await api.post("deleteMembershipApplication.php", { id });

      if (result?.success) {
        setItems((prev) => prev.filter((item) => item.id !== id));
      } else {
        alert(result?.message || "Delete failed");
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert("Something went wrong while deleting");
    }
  };

  const handlePayment = (app) => {
    navigate("/payment", {
      state: {
        application: app,
      },
    });
  };

  const getStatusBadge = (status) => {
    switch ((status || "").toLowerCase()) {
      case "approved":
        return "bg-green-100 text-green-700";
      case "rejected":
        return "bg-red-100 text-red-700";
      case "pending":
      default:
        return "bg-yellow-100 text-yellow-700";
    }
  };

  const getPaymentBadge = (payment) => {
    switch ((payment || "").toLowerCase()) {
      case "paid":
        return "bg-green-100 text-green-700";
      case "failed":
        return "bg-red-100 text-red-700";
      case "unpaid":
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <section className="bg-white rounded-2xl shadow-md p-4 sm:p-6">
      <h2 className="text-xl sm:text-2xl font-bold text-brand mb-2">
        My Membership Applications
      </h2>

      <p className="text-sm sm:text-base text-gray-600 mb-5 sm:mb-6">
        View and manage your submitted memberships.
      </p>

      {loading && <p className="text-sm sm:text-base">Loading...</p>}

      {!loading && error && (
        <p className="text-sm sm:text-base text-red-600">{error}</p>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="border border-dashed border-gray-300 rounded-xl p-6 sm:p-8 text-center text-sm sm:text-base text-gray-500">
          No membership applications found.
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="space-y-4">
          {items.map((app) => {
            const isPaid = (app.payment_status || "").toLowerCase() === "paid";

            return (
              <div
                key={app.id}
                className="border border-gray-200 rounded-xl p-4 sm:p-5"
              >
                <div className="flex flex-col gap-4 sm:gap-3 md:flex-row md:items-start md:justify-between">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                      {app.membership_type}
                    </h3>

                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="text-xs text-gray-500 self-center">
                        Status
                      </span>
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadge(
                          app.status || "Pending"
                        )}`}
                      >
                        {app.status || "Pending"}
                      </span>

                      <span className="text-xs text-gray-500 self-center sm:ml-2">
                        Payment
                      </span>
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${getPaymentBadge(
                          app.payment_status || "Unpaid"
                        )}`}
                      >
                        {app.payment_status || "Unpaid"}
                      </span>
                    </div>

                    <p className="mt-3 text-xs sm:text-sm text-gray-500 break-words">
                      Submitted: {app.created_at || "N/A"}
                    </p>
                  </div>

                  <div className="text-left md:text-right shrink-0">
                    <div className="text-base sm:text-lg font-bold text-green-700">
                      ${app.amount}
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
                  {!isPaid ? (
                    <>
                      <span
                        onClick={() => handleEdit(app)}
                        className="text-blue-600 hover:underline cursor-pointer"
                      >
                        Edit
                      </span>

                      <span className="hidden sm:inline text-gray-300">|</span>

                      <span
                        onClick={() => handleDelete(app.id)}
                        className="text-red-600 hover:underline cursor-pointer"
                      >
                        Delete
                      </span>

                      <span className="hidden sm:inline text-gray-300">|</span>

                      <span
                        onClick={() => handlePayment(app)}
                        className="text-green-600 hover:underline cursor-pointer font-medium"
                      >
                        Pay Now
                      </span>
                    </>
                  ) : (
                    <span className="text-green-600 font-medium text-sm">
                      Payment Completed ✔
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}